import { useEffect, useState } from "react";
import BigButton from "components/atoms/buttons/BigButton";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { selectSubscriptionStomp } from "redux/stomp/stompSelector";
import * as mediaQuery from "components/MediaQuery";
import { useMemo } from "react";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useKeyType } from "hook/subscription/useKeyType";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useQuery } from "react-query";
import { getSubscriptonRequestList } from "api/subscriptionAPI";
import {
  SUBSCRIPTION_TYPE,
  SubscriptionPageOutletContext,
  SubscriptionRequestList,
  SubscriptionRequestListResponse,
  SubscriptionRequestTable,
} from "api/interfaces/subscription.interface";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { TableColumnsType } from "antd";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import {
  changeSortOrderAntd,
  CommonTable,
} from "components/atoms/table/AntdTable";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { formatNumber, formatUppercaseOnly } from "utils/functions";
import RequestKeyModal from "pages/subscription/modal/requestKey";
import SubscriptionRequestHistoryPresenter from "./SubscriptionRequestHistoryPresenter";

export default function SubscriptionRequestHistoryContainer() {
  const [request, setRequest] = useState<boolean>(false);
  const onModal = () => {
    setRequest(!request);
  };
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { subscriptionRequestUpdated, subscriptionConfirmUpdated } =
    useAppSelector(selectSubscriptionStomp);
  const isMobile = mediaQuery.useIsMobile();
  const isBigScreen = mediaQuery.useIsBigScreen();

  const { keyTypeOptions, durationMonthOptions } = useKeyType();
  const {
    onHeaderCell,
    onChangePage,
    setButtons,
    onClickFilterTitle,
    setStatus,
  } = useOutletContext<SubscriptionPageOutletContext>();
  const [pageInfo, setPageInfo] = useState<Partial<BasePagination>>({
    ...baseQueryInfo,
    sortType: "createdAt",
    sortDirection: SORT_DIRECTION.DESC,
  });
  const [searchParams] = useSearchParams();
  const queryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...baseQueryInfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("type") && { type: get("type") }),
    };
  }, [searchParams, pageInfo]);
  const queryKey = useQueryKey(queryInfo);

  const { data, isFetching, refetch } = useQuery(
    ["getSubscriptonRequestList", selectedAccount.accountId, queryKey],
    () =>
      getSubscriptonRequestList({
        accountId: selectedAccount.accountId,
        payload: queryInfo,
      }),
    {
      onSuccess: (res: SubscriptionRequestListResponse) => {
        if (res.page) {
          setStatus({
            totalTitle: "Total",
            totalValue: res.page.total,
          });
          setPageInfo((query) => {
            return {
              ...query,
              ...res.page,
            };
          });
        }
      },
      retry: 0,
      refetchOnWindowFocus: false,
    }
  );

  const columns: TableColumnsType<SubscriptionRequestTable> = [
    {
      title: (
        <TableColumnFilterTitle
          title="Type"
          filters={keyTypeOptions}
          onClick={(value: string | undefined) => {
            onClickFilterTitle(value, "type");
          }}
        />
      ),
      dataIndex: "keyType",
      render: (value, row) => formatUppercaseOnly(value),
      width: 90,
    },
    {
      title: "Requester",
      dataIndex: "requester",
      key: "requester",
    },
    {
      title: "Installer",
      dataIndex: "installer",
      key: "installer",
      width: isBigScreen ? "30%" : "20%",
      responsive: ["xxl"],
    },
    {
      title: "QTY",
      dataIndex: "qty",
      key: "qty",
      render: (value, row) => {
        let unit = "CH";
        if (row.keyType === SUBSCRIPTION_TYPE.STORAGE) {
          unit = "TB";
        }
        return `${formatNumber(value)} ${unit}`;
      },
      width: 100,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "duration"),
      key: "duration",
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
      render: (value, row) =>
        durationMonthOptions(row.keyType as SUBSCRIPTION_TYPE, true).find(
          (opt) => opt.value === String(value)
        )?.label || "-",
      width: isMobile ? 100 : 180,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      sorter: true,
      key: "createdAt",
      sortOrder: changeSortOrderAntd(queryInfo, "createdAt"),
      defaultSortOrder: "descend",
      onHeaderCell: onHeaderCell,
      render: (value, row) => {
        return getAccountTimeFormatDate(value, selectedAccount);
      },
      responsive: ["lg"],
      width: 100,
    },
    {
      title: "Request Status",
      dataIndex: "isConfirm",
      render: (value) => (value ? "Completed" : "Applied"),
      width: 130,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      responsive: ["xxl"],
      width: 200,
    },
  ];

  const requestList: SubscriptionRequestTable[] = useMemo(() => {
    if (data?.result) {
      return data.result.map((res: SubscriptionRequestList) => {
        return {
          ...res,
          qty:
            res.keyType === SUBSCRIPTION_TYPE.STORAGE
              ? res.capacity
              : res.channels,
        };
      });
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (
      (subscriptionRequestUpdated.updatedDate &&
        subscriptionRequestUpdated.accountId === selectedAccount.accountId) ||
      (subscriptionConfirmUpdated.updatedDate &&
        subscriptionConfirmUpdated.accountId === selectedAccount.accountId)
    ) {
      refetch();
    }
  }, [subscriptionRequestUpdated, subscriptionConfirmUpdated]);

  useEffect(() => {
    if (setButtons) {
      setButtons(
        <RBACWrapper
          requiredPermissions={[
            "manageBillingAndLicenses",
            "manageSubscription",
          ]}
        >
          <BigButton
            label="Request Key"
            onClickBtn={onModal}
            className="request-button"
            add
          />
        </RBACWrapper>
      );
    }
  }, [setButtons]);

  return (
    <SubscriptionRequestHistoryPresenter
      columns={columns}
      data={requestList}
      isFetching={isFetching}
      queryInfo={queryInfo}
      onChangePage={(page: number) => onChangePage(page, 0)}
      onModal={onModal}
      request={request}
    />
  );
}
